import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, asArray } from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/Primitives";
import { Server, Package, Boxes, Search, Plus, Pencil, Trash2, X, Save, UserRound, Check } from "lucide-react";

// Inventory management, four views:
//   Hardware        - enrolled devices + their fingerprint + who owns/uses each
//   Assigned assets - manually-registered peripherals (monitor, headset, …) per user
//   Software        - estate-wide installed-application rollup
//   Consumables     - the manual stock register (spare units)

const TABS = [
  ["hardware", "Hardware", Server],
  ["assigned", "Assigned assets", UserRound],
  ["software", "Software", Package],
  ["consumables", "Consumables", Boxes],
];

const PERIPHERAL_TYPES = ["Monitor", "Headset", "Keyboard", "Mouse", "Docking station",
  "Webcam", "Phone", "Tablet", "Printer", "External drive", "Other"];

export default function Inventory() {
  const [tab, setTab] = useState("hardware");
  const [agents, setAgents] = useState(null);
  const [software, setSoftware] = useState(null);
  const [items, setItems] = useState(null);
  const [peripherals, setPeripherals] = useState(null);
  const [q, setQ] = useState("");
  const [editOwner, setEditOwner] = useState(null); // agent id being edited
  const [ownerVal, setOwnerVal] = useState("");
  const [modal, setModal] = useState(null);         // peripheral draft {id?, ...}
  const nav = useNavigate();

  const loadAgents = () => api.get("/agents").then(r => setAgents(asArray(r.data))).catch(() => setAgents([]));
  const loadPeripherals = () => api.get("/peripherals").then(r => setPeripherals(asArray(r.data))).catch(() => setPeripherals([]));

  useEffect(() => {
    if ((tab === "hardware" || tab === "assigned") && !agents) loadAgents();
    if (tab === "assigned" && !peripherals) loadPeripherals();
    if (tab === "software" && !software) api.get("/software/estate").then(r => setSoftware(r.data?.titles || [])).catch(() => setSoftware([]));
    if (tab === "consumables" && !items) api.get("/inventory").then(r => setItems(asArray(r.data))).catch(() => setItems([]));
  }, [tab]); // eslint-disable-line

  const appCount = (a) => (a.last_inventory?.software || []).length;
  const filt = (s) => !q || (s || "").toLowerCase().includes(q.toLowerCase());

  const saveOwner = async (a) => {
    try {
      await api.put(`/agent/${a.id}/assigned-user`, { assigned_user: ownerVal });
      setAgents(list => list.map(x => x.id === a.id ? { ...x, assigned_user: ownerVal } : x));
      setEditOwner(null); toast.success("Owner updated");
    } catch { toast.error("Failed"); }
  };

  const savePeripheral = async () => {
    const d = modal;
    if (!d.type && !d.make_model) { toast.error("Pick a type or enter make/model"); return; }
    try {
      if (d.id) await api.put(`/peripherals/${d.id}`, d);
      else await api.post("/peripherals", d);
      toast.success("Saved"); setModal(null); loadPeripherals();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const delPeripheral = async (p) => {
    if (!window.confirm(`Delete ${p.type} ${p.serial_number || ""}?`)) return;
    try { await api.delete(`/peripherals/${p.id}`); toast.success("Deleted"); loadPeripherals(); } catch { toast.error("Failed"); }
  };
  const unassign = async (p) => {
    try { await api.put(`/peripherals/${p.id}`, { assigned_user: "", status: "unassigned" }); loadPeripherals(); toast.success("Unassigned"); } catch { toast.error("Failed"); }
  };

  const input = "bg-zinc-900 border border-zinc-800 rounded px-2.5 py-2 text-sm text-zinc-200 outline-none focus:border-zinc-600 w-full";

  return (
    <div className="p-6 md:p-8 max-w-[1200px] mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Inventory management</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1 text-zinc-50">Estate inventory</h1>
        </div>
        <div className="flex items-center gap-2">
          {tab === "assigned" && (
            <button onClick={() => setModal({ type: "Monitor", agent_id: "", assigned_user: "", make_model: "", serial_number: "", notes: "" })}
              className="inline-flex items-center gap-1.5 bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/40 text-blue-200 rounded-md px-3 py-1.5 text-sm"><Plus size={15} /> Add asset</button>
          )}
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-zinc-600" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter…"
              className="bg-zinc-950 border border-zinc-800 rounded-md pl-8 pr-3 py-1.5 text-sm text-zinc-200 outline-none focus:border-zinc-600" />
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-zinc-900 mt-4">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)}
            className={"flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px " + (tab === key ? "border-blue-500 text-white" : "border-transparent text-zinc-500 hover:text-zinc-200")}>
            <Icon size={14} />{label}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {/* HARDWARE */}
        {tab === "hardware" && (
          agents === null ? <Loading /> :
          agents.length === 0 ? <Empty text="No devices enrolled yet — hand out an installer link and they'll appear here." /> :
          <Table head={["Device", "Owned by", "OS", "Serial", "Vendor / model", "Apps", "Status"]}>
            {agents.filter(a => filt(a.hostname) || filt(a.os) || filt(a.assigned_user)).map(a => (
              <tr key={a.id} className="border-b border-zinc-900/60 hover:bg-zinc-900/40">
                <td className="px-4 py-2.5 text-zinc-100 font-medium cursor-pointer" onClick={() => nav(`/agents/${a.id}`)}>{a.hostname}</td>
                <td className="px-4 py-2.5">
                  {editOwner === a.id ? (
                    <span className="flex items-center gap-1">
                      <input autoFocus value={ownerVal} onChange={e => setOwnerVal(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && saveOwner(a)}
                        className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs w-40 outline-none" placeholder="name / email" />
                      <button onClick={() => saveOwner(a)} className="text-emerald-400 p-1"><Check size={13} /></button>
                      <button onClick={() => setEditOwner(null)} className="text-zinc-500 p-1"><X size={13} /></button>
                    </span>
                  ) : (
                    <button onClick={() => { setEditOwner(a.id); setOwnerVal(a.assigned_user || ""); }}
                      className="inline-flex items-center gap-1.5 text-sm group">
                      {a.assigned_user
                        ? <span className="text-zinc-200">{a.assigned_user}</span>
                        : <span className="text-zinc-600 italic">Unassigned</span>}
                      <Pencil size={11} className="text-zinc-700 group-hover:text-zinc-400" />
                    </button>
                  )}
                </td>
                <td className="px-4 py-2.5 text-zinc-400 cursor-pointer" onClick={() => nav(`/agents/${a.id}`)}>{a.os} {a.os_version}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{a.serial_number || "—"}</td>
                <td className="px-4 py-2.5 text-zinc-400">{[a.manufacturer, a.model].filter(Boolean).join(" ") || "—"}</td>
                <td className="px-4 py-2.5 font-mono text-zinc-300">{appCount(a) || "—"}</td>
                <td className="px-4 py-2.5"><StatusBadge status={a.status === "online" ? "online" : "offline"} /></td>
              </tr>
            ))}
          </Table>
        )}

        {/* ASSIGNED ASSETS (peripherals) */}
        {tab === "assigned" && (
          peripherals === null ? <Loading /> :
          peripherals.length === 0 ? <Empty text="No assigned assets yet. Use “Add asset” to register a monitor, headset, dock or phone against a user — with its serial number." /> :
          <Table head={["Type", "Make / model", "Serial", "Owned by", "Device", "Status", ""]}>
            {peripherals.filter(p => filt(p.type) || filt(p.make_model) || filt(p.serial_number) || filt(p.assigned_user)).map(p => (
              <tr key={p.id} className="border-b border-zinc-900/60 hover:bg-zinc-900/40">
                <td className="px-4 py-2.5 text-zinc-100">{p.type}</td>
                <td className="px-4 py-2.5 text-zinc-400">{p.make_model || "—"}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{p.serial_number || "—"}</td>
                <td className="px-4 py-2.5 text-zinc-300">{p.assigned_user || <span className="text-zinc-600 italic">Unassigned</span>}</td>
                <td className="px-4 py-2.5 text-zinc-400 cursor-pointer" onClick={() => p.agent_id && nav(`/agents/${p.agent_id}`)}>{p.hostname || "—"}</td>
                <td className="px-4 py-2.5"><StatusBadge status={p.status === "assigned" ? "active" : "retired"} /></td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1 justify-end">
                    {p.status === "assigned" && <button onClick={() => unassign(p)} title="Unassign" className="text-[11px] text-amber-400/80 hover:text-amber-300 px-1.5">Unassign</button>}
                    <button onClick={() => setModal({ ...p })} className="p-1 text-zinc-500 hover:text-zinc-200"><Pencil size={13} /></button>
                    <button onClick={() => delPeripheral(p)} className="p-1 text-red-400/70 hover:text-red-300"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}

        {/* SOFTWARE */}
        {tab === "software" && (
          software === null ? <Loading /> :
          software.length === 0 ? <Empty text="No software inventory yet — it populates from each device's first sweep after enrolment." /> :
          <>
            <div className="text-[11px] text-zinc-500 mb-2 font-mono">{software.length} distinct applications across the estate · click a device on the Hardware tab for its full list</div>
            <Table head={["Application", "Installs", "Versions"]}>
              {software.filter(s => filt(s.name)).slice(0, 500).map((s, i) => (
                <tr key={i} className="border-b border-zinc-900/60 hover:bg-zinc-900/40">
                  <td className="px-4 py-2.5 text-zinc-100">{s.name}</td>
                  <td className="px-4 py-2.5 font-mono text-zinc-300">{s.installs}</td>
                  <td className="px-4 py-2.5 text-xs text-zinc-500 truncate max-w-md">{(s.versions || []).join(", ") || "—"}</td>
                </tr>
              ))}
            </Table>
          </>
        )}

        {/* CONSUMABLES */}
        {tab === "consumables" && (
          items === null ? <Loading /> :
          items.length === 0 ? <Empty text="No consumables tracked. This is the manual stock register (spare units) — separate from what agents discover." /> :
          <Table head={["Name", "SKU", "Warehouse", "Qty", "Status"]}>
            {items.filter(i => filt(i.name)).slice(0, 300).map(i => (
              <tr key={i.id} className="border-b border-zinc-900/60 hover:bg-zinc-900/40">
                <td className="px-4 py-2.5 text-zinc-100">{i.name}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{i.sku || "—"}</td>
                <td className="px-4 py-2.5 text-zinc-400">{i.warehouse || "—"}</td>
                <td className="px-4 py-2.5 font-mono">{i.qty_on_hand ?? "—"}</td>
                <td className="px-4 py-2.5">{i.status ? <StatusBadge status={i.status} /> : "—"}</td>
              </tr>
            ))}
          </Table>
        )}
      </div>

      {/* add / edit peripheral */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setModal(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-zinc-950 border border-zinc-800 rounded-lg shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-900">
              <div className="text-sm font-medium text-zinc-100">{modal.id ? "Edit asset" : "Add assigned asset"}</div>
              <button onClick={() => setModal(null)} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Type">
                  <select className={input} value={modal.type} onChange={e => setModal(m => ({ ...m, type: e.target.value }))}>
                    {PERIPHERAL_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Serial number">
                  <input className={input} value={modal.serial_number} onChange={e => setModal(m => ({ ...m, serial_number: e.target.value }))} placeholder="SN-…" />
                </Field>
              </div>
              <Field label="Make / model"><input className={input} value={modal.make_model} onChange={e => setModal(m => ({ ...m, make_model: e.target.value }))} placeholder="Dell U2723QE" /></Field>
              <Field label="Device">
                <select className={input} value={modal.agent_id || ""} onChange={e => {
                  const aid = e.target.value; const a = (agents || []).find(x => x.id === aid);
                  setModal(m => ({ ...m, agent_id: aid, assigned_user: m.assigned_user || (a?.assigned_user || "") }));
                }}>
                  <option value="">— none —</option>
                  {(agents || []).map(a => <option key={a.id} value={a.id}>{a.hostname}{a.assigned_user ? ` · ${a.assigned_user}` : ""}</option>)}
                </select>
              </Field>
              <Field label="Owned by (user)"><input className={input} value={modal.assigned_user} onChange={e => setModal(m => ({ ...m, assigned_user: e.target.value }))} placeholder="name / email" /></Field>
              <Field label="Notes"><input className={input} value={modal.notes} onChange={e => setModal(m => ({ ...m, notes: e.target.value }))} /></Field>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setModal(null)} className="px-3.5 py-2 text-sm text-zinc-400 hover:text-zinc-200">Cancel</button>
                <button onClick={savePeripheral} className="inline-flex items-center gap-2 bg-blue-500/15 border border-blue-500/40 text-blue-200 rounded-md px-4 py-2 text-sm"><Save size={15} /> Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">{label}</div>
      {children}
    </div>
  );
}
function Table({ head, children }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-zinc-900/40 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          <tr>{head.map((h, i) => <th key={i} className="text-left px-4 py-3 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
function Loading() { return <div className="text-zinc-500 text-sm p-6">Loading…</div>; }
function Empty({ text }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 border-dashed rounded-md p-10 text-center">
      <Package className="mx-auto text-zinc-700 mb-3" size={26} />
      <div className="text-zinc-500 text-sm max-w-md mx-auto">{text}</div>
    </div>
  );
}
